import { trimString, isOfType, isArrayEmpty, isObjectEmpty } from './helpers';

const WWW = 'www';

const QUESTION_TITLE_ERROR = 'error_question_title';
const ANSWER_TITLE_ERROR = 'error_answer_title';

const PUBLIC_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'aol.com',
  'mail.com',
  'gmx.com',
  'yandex.com',
  'zoho.com',
  'protonmail.com',
  'proton.me',
  'protonmail.ch',
  'fastmail.com',
  'hushmail.com',
  'tutanota.com',
  'mail.ru',
  'gawab.com',
  'inbox.com',
  'me.com',
  'msn.com',
  'cox.net',
  'verizon.net',
];
const DISPOSABLE_EMAIL_DOMAINS = [
  '10minutemail.com',
  'guerrillamail.com',
  'mailinator.com',
  'trashmail.com',
  'temp-mail.org',
  'emailondeck.com',
  'maildrop.cc',
  'getnada.com',
  'yopmail.com',
  'dispostable.com',
  'throwawaymail.com',
  'fakeinbox.com',
  'mintemail.com',
  'moakt.com',
  'sharklasers.com',
  'anonbox.net',
  'spambog.com',
  'tempmail.net',
  'mailtemp.net',
  'mytemp.email',
  'discard.email',
  'fakeemail.net',
  'mailcatch.com',
  'mohmal.com',
  'spamgourmet.com',
  'spam4.me',
  'tempinbox.com',
  'temp-mail.io',
  'mytrashmail.com',
];

export const isEmpty = value =>
  value !== undefined && value !== null && value !== '';

export function validateLength(value, min = 0, max = 100, maxOff = false) {
  const pattern = !maxOff ? `^[\\s\\S]{${min},${max}}$` : `^[\\s\\S]{${min},}$`;
  return new RegExp(pattern).test(value);
}

export const isRichTextEditorEmpty = value => {
  return isEmpty(value?.replace(/<[^>]+>/g, '').trim());
};

export function validateRange(values) {
  if (isArrayEmpty(values)) {
    return true;
  }
  const isNotValid = values?.some(value => {
    const minValue = value?.range_from;
    const maxValue = value?.range_to;
    return maxValue <= minValue;
  });
  return !isNotValid;
}

export function validateLowercase(value) {
  return /[a-z]/.test(value);
}

export function validateUppercase(value) {
  return /[A-Z]/.test(value);
}

export function validateWhitespace(value) {
  return /^\S*$/.test(value);
}

export function validateTypeOfCharacters(value) {
  return /^.*[\d~`!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/].*/.test(value);
}

export function validateEmail(value) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value);
}

export function validateBusinessEmail(value) {
  const domains = [...PUBLIC_EMAIL_DOMAINS, ...DISPOSABLE_EMAIL_DOMAINS];

  const emailDomains = domains.map(domain => domain.replace(/\./g, '\\.'));
  const negativeLookahead = `(?!(?:${emailDomains.join('|')})$)`;
  const pattern = `^(?!.*\\.\\.)[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]{0,62}[a-zA-Z0-9])?@${negativeLookahead}(?:[a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,10}$`;
  const emailRegex = new RegExp(pattern, 'i');

  return emailRegex.test(value);
}

export function validateName(value) {
  return /^[\p{L} '-]*$/u.test(value);
}

export function validateFullName(value) {
  const parts = trimString(value).split(/\s+/).filter(Boolean);

  return parts.length >= 2;
}

export function forbiddenCharacters(value) {
  return /^[^<>[\]{}\\|]*$/.test(value);
}

export function validatePhone(value) {
  if (!/^\+?[0-9\s()-]+$/.test(value)) return false;

  const filteredDigits = value.replace(/\D/g, '');
  return filteredDigits.length >= 7 && filteredDigits.length <= 15;
}

export function validatePassword(value) {
  // Minimum six characters, at least one letter and one number:
  return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d~`!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/]{6,}$/.test(
    value
  );
}

export function validateOrganizationUrl(value) {
  const pattern = '^[a-z]+[a-z\\d]*((?:-(?=[a-z\\d])|[a-z\\d])*)$';

  return new RegExp(pattern).test(value) && value !== WWW;
}

export function validateUrl(value) {
  return /^((ftp|http|https):\/\/)?([A-z 0-9-]+)\.([A-z]{2,})/.test(value);
}

export const validateFileType = (value, fileTypes) => {
  if (value instanceof File) {
    return fileTypes.includes(value.type);
  }

  return true;
};

export const validateFileSize = (value, maxSize) => {
  if (value instanceof File) {
    return value.size <= maxSize;
  }

  return true;
};

export const validateReviews = reviews => {
  if (isArrayEmpty(reviews)) return null;

  const errors = reviews.reduce((acc, review) => {
    return {
      ...acc,
      ...((review.reviewers && isArrayEmpty(review.reviewers)) ||
      (review.subjects && isArrayEmpty(review.subjects))
        ? { [review.attribute || review.questionnaire]: 'reviews' }
        : {}),
    };
  }, {});

  return isObjectEmpty(errors) ? null : errors;
};

export const validateQuestionFields = (
  questions,
  titleValidators,
  answerValidators
) => {
  const errors = questions.reduce((acc, curr, index) => {
    const answersErrors = curr.answers.reduce((accum, current, indx) => {
      const answerError = answerValidators.find(
        val => !val.validator(current.text)
      );

      return {
        ...accum,
        ...(answerError && !isObjectEmpty(answerError)
          ? { [`${ANSWER_TITLE_ERROR}_${indx}`]: answerError.type }
          : {}),
      };
    }, {});

    const titleError = titleValidators.find(val => !val.validator(curr.text));
    const hasTitleError = titleError && !isObjectEmpty(titleError);
    const hasAnswerError = !isObjectEmpty(answersErrors);

    return {
      ...acc,
      ...(hasTitleError || hasAnswerError
        ? {
            [index]: {
              ...(hasTitleError
                ? { [QUESTION_TITLE_ERROR]: titleError.type }
                : {}),
              ...(hasAnswerError ? answersErrors : {}),
            },
          }
        : {}),
    };
  }, {});

  return isObjectEmpty(errors) ? false : errors;
};

export const validateRequiredMultipleDates = dates => {
  const errors = dates.reduce((acc, date) => {
    return [...acc, ...(date === null ? ['required'] : [null])];
  }, []);

  return errors.some(currentError => currentError !== null) ? errors : null;
};

export const validateUniqueMultipleDates = dates => {
  const errors = dates.reduce((acc, date, currentIndex) => {
    return [
      ...acc,
      ...(dates.some(
        (currentDate, index) => date === currentDate && index !== currentIndex
      )
        ? ['uniqueDate']
        : [null]),
    ];
  }, []);

  return errors.some(currentError => currentError !== null) ? errors : null;
};

export const validateField = async (
  field,
  value,
  initialValue,
  values,
  canSkipValidation
) => {
  let error = null;
  const asyncValidations = [];
  const errorTypes = [];
  const fieldValue =
    isOfType(value, 'string') && !field.forbidTrim ? trimString(value) : value;

  if (field.validators && field.validators.length > 0) {
    if (
      initialValue &&
      isOfType(fieldValue, 'string') &&
      fieldValue === initialValue &&
      !field.shouldValidateInitialValue
    ) {
      return error;
    }

    for (let i = 0; i < field.validators.length; i += 1) {
      if (canSkipValidation && field.validators[i].shouldSkipValidation) {
        return error;
      }
      if (field.validators[i].async) {
        errorTypes.push(field.validators[i].type);
        asyncValidations.push(
          field.validators[i].validator(
            fieldValue,
            field.validators[i].shouldPassFormValues ? values : undefined
          )
        );
        break;
      } else if (field.validators[i].isMultiple) {
        const errors = field.validators[i].validator(
          fieldValue,
          field.validators[i].shouldPassFormValues ? values : undefined
        );
        if (errors) {
          error = errors;
          break;
        }
      } else {
        const isValid = field.validators[i].validator(
          fieldValue,
          field.validators[i].shouldPassFormValues ? values : undefined
        );
        if (
          !isValid &&
          (field.required ||
            (field.parent && values?.[field.parent.name]) ||
            fieldValue)
        ) {
          error = field.validators[i].type;
          break;
        }
      }
    }
  }

  if (!error && asyncValidations.length > 0) {
    await Promise.all(asyncValidations).then(results => {
      const errorIndex = results.findIndex(item => !item);

      if (errorIndex !== -1) {
        error = errorTypes[errorIndex];
      }
    });
  }

  return error;
};

export const validateFields = async (
  fields,
  values,
  initialValues = {},
  canSkipValidation = false
) => {
  const errors = {};
  const validations = [];

  fields.forEach(field => {
    if (!field.isDisabled || field.shouldValidateDisabledField) {
      validations.push(
        validateField(
          field,
          field?.fieldGroup
            ? values[field.fieldGroup]?.[field?.name]
            : values[field.name],
          field?.fieldGroup
            ? initialValues[field.fieldGroup]?.[field?.name]
            : initialValues[field.name],
          values,
          canSkipValidation
        )
      );
    } else {
      validations.push(null);
    }
  });

  await Promise.all(validations).then(results => {
    results.forEach((result, index) => {
      if (result) {
        if (fields[index].fieldGroup) {
          errors[fields[index].fieldGroup] = {
            ...errors[fields[index].fieldGroup],
            [fields[index].name]: result,
          };
        } else {
          errors[fields[index].name] = result;
        }
      }
    });
  });

  return errors;
};

export const validateAll = (fields, values, translations) => {
  const errors = {};

  fields.forEach(field => {
    if (
      Object.keys(errors).length === 0 &&
      field.validators &&
      (field.required || values[field.name])
    ) {
      field.validators.forEach(property => {
        if (!errors[field.name] && !property.validator(values[field.name])) {
          errors[field.name] = translations[property.translationKey];
        }
      });
    }
  });

  return errors;
};

export const validateFieldSync = (field, value, values) => {
  let error = null;
  const fieldValue = isOfType(value, 'string') ? trimString(value) : value;

  if (field.validators && field.validators.length > 0) {
    for (let i = 0; i < field.validators.length; i += 1) {
      const isValid = field.validators[i].validator(
        fieldValue,
        field.validators[i].shouldPassFormValues ? values : undefined
      );
      if (
        !isValid &&
        (field.required ||
          (field.parent && values?.[field.parent.name]) ||
          fieldValue)
      ) {
        error = field.validators[i].type;
        break;
      }
    }
  }

  return error;
};

export const validateFieldsInList = async (values, fields) => {
  const errors = {};

  for (let i = 0; i < values.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const fieldErrors = await validateFields(fields, values[i]);

    if (!isObjectEmpty(fieldErrors)) {
      errors[i] = fieldErrors;
    }
  }

  return !isObjectEmpty(errors) ? errors : null;
};

export const validateMultipleField = async (values, fields) => {
  const errors = {};

  for (let i = 0; i < values.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const absenceErrors = await validateFields(fields, values[i]);

    if (!isObjectEmpty(absenceErrors)) {
      errors[values[i]?.id || values[i]?.tempId] = absenceErrors;
    }
  }

  return !isObjectEmpty(errors) ? errors : null;
};
